import type { AppointmentRole, Me } from './endpoints.ts'

// The roles come from the backend, so they live in the contract package and not per app.
// Why, and how each one is derived: README, «Ruoli: perché stanno qui, e come si derivano».

export const ROLES = ['staff', 'club', 'coach', 'parent', 'player', 'guest'] as const

export type Role = (typeof ROLES)[number]

/** The roles that get you into the back-office. */
export const BACKOFFICE_ROLES: readonly Role[] = ['staff']

/** True for an incarico held on a bench: `coach` and `team_official`, the dirigente di squadra. Why this
 *  stays a name: the README, «`isCoachingRole`, e perché resta un nome invece di un confronto». */
export function isCoachingRole(role: AppointmentRole): boolean {
  return role === 'coach' || role === 'team_official'
}

/** An incarico, as far as «does it hold a place» needs: the role and the seat flag. */
export interface SeatedAppointment {
  readonly role: AppointmentRole
  readonly seat: boolean
}

/** Whether an incarico holds a seat, which is what opens anything of the società: a dirigente di società
 *  always does, a bench incarico only with `seat`. The mirror of `holds_a_seat` in `core/authz.py`, and what
 *  README says of it: «Un posto, e perché ogni specchio lo chiede». */
export function holdsASeat(held: SeatedAppointment): boolean {
  return !isCoachingRole(held.role) || held.seat
}

export const APPOINTMENT_BANDS = ['club', 'team', 'unassigned'] as const

export type AppointmentBand = (typeof APPOINTMENT_BANDS)[number]

/** Which band an incarico is read in. Why a null `team_id` cannot answer it alone, and what it
 *  cost when two screens answered it separately: the README, «`appointmentBandOf`». */
export function appointmentBandOf(
  role: AppointmentRole,
  teamId: number | null | undefined,
): AppointmentBand {
  if (teamId != null) return 'team'
  return isCoachingRole(role) ? 'unassigned' : 'club'
}

/** An incarico, as far as reading its state needs. Structural, because `AppointmentBrief` and
 *  `AppointmentRead` both fit and neither is a subtype of the other. */
export interface StatefulAppointment {
  readonly ended_on?: string | null
  /** Absent where the view already names one season — a club's organigramma. There `ended_on`
   *  is the whole truth; the two unscoped views carry `AppointmentBrief`, which requires it. */
  readonly season?: { readonly ends_on: string }
}

/** True while an incarico is still held. `today` is passed in rather than read: this package
 *  has no clock, and the caller already has one. Why the season counts as much as the end
 *  date: services/api/docs/overview.md, `status_conditions`. */
export function isHeld(one: StatefulAppointment, today: string): boolean {
  if (one.ended_on != null) return false
  return one.season === undefined || one.season.ends_on >= today
}

function holdsAnOfficeIncarico(me: Me): boolean {
  return me.appointments.some((held) => !isCoachingRole(held.role))
}

/** A bench profile comes from a seat: staff of a squad without one is a name and opens no page. */
function holdsABenchSeat(me: Me): boolean {
  return me.appointments.some((held) => isCoachingRole(held.role) && holdsASeat(held))
}

/** `follows` came with spec #278, and an API deployed before it does not send it: the frontend
 *  can be ahead (docs/sviluppo/trappole.md § «Il deploy dei frontend e dell'API possono divergere»). */
function followsSomebody(me: Me): boolean {
  const follows: Me['follows'] | undefined = me.follows
  return follows != null && follows.length > 0
}

/** The profiles an account really holds, before `guest` and `parent` fill an empty hand. */
function profilesHeld(me: Me): readonly Role[] {
  const staff: readonly Role[] = me.staff_role != null ? ['staff'] : []
  const football: Role[] = []
  if (holdsAnOfficeIncarico(me)) football.push('club')
  if (holdsABenchSeat(me)) football.push('coach')
  if (me.guardian_of.length > 0 || followsSomebody(me)) football.push('parent')
  if (me.is_player) football.push('player')
  return [...staff, ...football]
}

/** What an account holds when it holds no profile: a person with nothing else is a parent
 *  with no children yet, no person at all is a guest. ADR 0003. */
function whenNoProfile(me: Me): readonly Role[] {
  return me.person_id != null ? ['parent'] : ['guest']
}

/** The facets held, without the bypass's widening: a menu built on `rolesOf` offers a
 *  presidente «La tua famiglia». Guards keep `rolesOf` — see the README. */
export function heldFacetsOf(me: Me | null): readonly Role[] {
  if (me === null) return []
  const held = profilesHeld(me)
  return held.length > 0 ? held : whenNoProfile(me)
}

/** The current user's roles, derived from the facets `/v1/me` reports — each of which expires
 *  on its own, because each is a row with a validity. See the README, «`rolesOf`». */
export function rolesOf(me: Me | null): readonly Role[] {
  if (me === null) return []
  if (me.is_dev_bypass) {
    const staff: readonly Role[] = me.staff_role != null ? ['staff'] : []
    return [...staff, 'club', 'coach', 'parent', 'player']
  }
  return heldFacetsOf(me)
}

/** Whether a real Cognito identity is behind this account: false under the dev bypass, where
 *  no token was ever minted. Here and not per app, like `rolesOf` above, because two surfaces
 *  asking the same question must not answer it differently. */
export function hasCognitoSession(me: Me): boolean {
  return !me.is_dev_bypass
}

/** True when the user holds at least one of the required roles. No requirement = pass. */
export function hasAnyRole(userRoles: readonly Role[], requiredRoles: readonly Role[]): boolean {
  if (requiredRoles.length === 0) return true
  return requiredRoles.some((required) => userRoles.includes(required))
}
